import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, ArrowRight } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";

export default function UploadPage() {
  const [kind, setKind] = useState("inventory");
  const [uploads, setUploads] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const nav = useNavigate();

  const load = () => api.get("/uploads").then(r => setUploads(r.data));
  useEffect(() => { load(); }, []);

  const handleFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    setBusy(true);
    try {
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${data.filename} · ${data.row_count} rows`);
      nav(`/app/upload/${data.upload_id}/map`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); await load(); }
  };

  return (
    <DashboardLayout>
      <h1 className="text-2xl sm:text-3xl font-bold" data-testid="upload-heading">Uploads</h1>
      <p className="mt-1 text-sm text-[#3B424B]">CSV or XLSX. Column mapping happens in the next step.</p>

      <div className="mt-6 tm-card p-6 bg-white">
        <div className="inline-flex bg-[var(--tm-off)] rounded-lg p-1 border border-[var(--tm-border)]">
          {["inventory", "sales"].map((k) => (
            <button key={k} onClick={() => setKind(k)} className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${kind === k ? "bg-white text-[#0F1115] shadow-sm" : "text-[#3B424B]"}`} data-testid={`upload-kind-${k}`}>
              {k === "inventory" ? "Inventory" : "Sales / POS"}
            </button>
          ))}
        </div>

        <div className="mt-6 border-2 border-dashed border-[var(--tm-border-strong)] rounded-xl p-10 text-center bg-[var(--tm-off)]" data-testid="upload-dropzone">
          <div className="mx-auto h-12 w-12 rounded-lg bg-[var(--tm-charcoal)] text-white flex items-center justify-center"><UploadCloud size={22} /></div>
          <div className="mt-4 font-semibold">Drop a {kind === "inventory" ? "inventory" : "sales/POS"} file here</div>
          <div className="text-sm text-[#3B424B]">Or click to select. .csv or .xlsx</div>
          <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="upload-file-input" />
          <button className="tm-btn-gold mt-5" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="upload-select">{busy ? "Uploading…" : "Select file"}</button>
        </div>
      </div>

      <div className="mt-8">
        <h2 className="text-lg font-bold">Recent uploads</h2>
        <div className="mt-3 tm-card bg-white overflow-hidden">
          {uploads.length === 0 ? (
            <div className="p-6 text-sm text-[#3B424B]">No uploads yet.</div>
          ) : (
            <div className="overflow-x-auto tm-scroll">
              <table className="w-full text-sm">
                <thead className="bg-[var(--tm-off)]">
                  <tr className="text-left text-xs uppercase tracking-wider text-[#3B424B]">
                    <th className="p-4">File</th><th className="p-4">Type</th><th className="p-4">Rows</th><th className="p-4">Status</th><th className="p-4">Uploaded</th><th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {uploads.map((u) => (
                    <tr key={u.id} className="border-t border-[var(--tm-border)]" data-testid={`upload-row-${u.id}`}>
                      <td className="p-4 flex items-center gap-2 font-medium"><FileSpreadsheet size={16} className="text-[#3B424B]" />{u.filename}</td>
                      <td className="p-4 capitalize">{u.kind}</td>
                      <td className="p-4 font-mono">{u.row_count}</td>
                      <td className="p-4">{u.mapped ? <span className="tm-badge bg-[#14624A]/10 text-[#14624A]">Mapped</span> : <span className="tm-badge bg-[#C9943D]/15 text-[#8a6318]">Needs mapping</span>}</td>
                      <td className="p-4 text-[#3B424B]">{new Date(u.created_at).toLocaleString()}</td>
                      <td className="p-4 text-right"><Link to={`/app/upload/${u.id}/map`} className="tm-link">{u.mapped ? "Review" : "Map columns"} <ArrowRight size={12} className="inline" /></Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
